import { useState, useEffect } from 'react';
import { CreditCard, Wallet as WalletIcon, X, Loader2, Zap, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

/**
 * Payment chooser for the gift-card Buy Now flow.
 *
 * Both methods are always visible and mutually exclusive. The earlier version
 * showed wallet as a checkbox and only revealed "Online Payment" once it was
 * unticked — so a customer with a wallet balance never learned the other option
 * existed. A choice you have to discover by undoing something is not a choice.
 *
 * **COD is deliberately absent.** A gift card is delivered digitally the moment
 * payment clears; there is no courier and no delivery event, so cash on delivery
 * would mean issuing a live, spendable voucher against a payment that never
 * happens. Physical products keep COD on CheckoutPage, where it makes sense.
 */
export function GiftCardPaymentModal({
  open,
  onClose,
  onConfirm,
  productName,
  amount,
  quantity = 1,
  walletBalance = 0,
  processing = false,
  formatPrice,
}) {
  const total = amount * quantity;
  const hasWallet = walletBalance > 0;

  const [method, setMethod] = useState('online');

  // A fresh Buy Now shouldn't inherit the last attempt's choice — especially
  // "wallet" after that balance has already been spent.
  useEffect(() => {
    if (open) setMethod(hasWallet ? 'wallet' : 'online');
  }, [open, hasWallet]);

  if (!open) return null;

  const walletApplied = method === 'wallet' ? Math.min(walletBalance, total) : 0;
  const amountDue = Math.max(0, total - walletApplied);
  const fullyPaidByWallet = walletApplied > 0 && amountDue === 0;

  const handleConfirm = () => {
    onConfirm({
      // Only "wallet" when it actually clears the whole bill. A partial wallet
      // payment is still a gateway order that happens to carry a wallet amount.
      paymentMethod: fullyPaidByWallet ? 'wallet' : 'online',
      walletAmount: walletApplied,
      total,
    });
  };

  /**
   * The selectable area is the button; `action` sits beside it, never inside.
   * An <a> nested in a <button> is invalid HTML and leaves screen readers with
   * two overlapping controls where the user sees one.
   */
  const Option = ({ value, icon: Icon, title, subtitle, note, disabled, action }) => {
    const selected = method === value && !disabled;
    return (
      <div
        className={`flex items-center gap-3 rounded-2xl border-2 pr-4 transition-all ${
          selected
            ? 'border-primary bg-primary/5'
            : disabled
              ? 'border-gray-200 bg-gray-50/60'
              : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
      >
        <button
          type="button"
          onClick={() => !disabled && setMethod(value)}
          disabled={disabled}
          aria-pressed={selected}
          className="flex flex-1 min-w-0 items-center gap-4 p-4 text-left disabled:cursor-default"
          data-testid={`giftcard-payment-${value}`}
        >
          <span
            className={`grid place-items-center w-5 h-5 rounded-full border-2 shrink-0 transition-colors ${
              selected ? 'border-primary bg-primary' : 'border-gray-300'
            }`}
          >
            {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} aria-hidden="true" />}
          </span>
          <Icon
            className={`w-5 h-5 shrink-0 ${selected ? 'text-primary' : disabled ? 'text-muted-foreground' : 'text-secondary'}`}
            aria-hidden="true"
          />
          <span className="flex-1 min-w-0">
            <span className={`block font-body font-semibold ${disabled ? 'text-muted-foreground' : 'text-secondary'}`}>
              {title}
            </span>
            <span className="block text-sm text-muted-foreground font-body">{subtitle}</span>
            {note && <span className="mt-1 block text-xs font-body text-primary">{note}</span>}
          </span>
        </button>
        {action}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 pb-4 sticky top-0 bg-white">
          <div className="min-w-0">
            <h2 className="font-heading text-xl text-secondary">Choose payment</h2>
            <p className="text-sm text-muted-foreground font-body line-clamp-1">
              {productName} · {quantity > 1 ? `${quantity} × ` : ''}{formatPrice(amount)}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={processing}
            className="p-2 -mr-2 rounded-full hover:bg-surface disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-6 space-y-3">
          <Option
            value="wallet"
            icon={WalletIcon}
            title="NSO Wallet"
            subtitle={`${formatPrice(walletBalance)} available`}
            disabled={!hasWallet}
            note={
              hasWallet && walletBalance < total
                ? `Covers ${formatPrice(walletBalance)} — the remaining ${formatPrice(total - walletBalance)} goes to online payment`
                : undefined
            }
            action={
              !hasWallet && (
                <Link
                  to="/dashboard/wallet"
                  className="shrink-0 rounded-full border border-gray-200 bg-white px-3 py-1.5 font-body text-xs font-medium text-secondary transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  Add money
                </Link>
              )
            }
          />

          <Option
            value="online"
            icon={CreditCard}
            title="Online Payment"
            subtitle="UPI, Cards, Netbanking"
          />

          <div className="rounded-2xl bg-surface p-4 space-y-2 text-sm font-body">
            <div className="flex justify-between text-muted-foreground">
              <span>Gift card value{quantity > 1 ? ` × ${quantity}` : ''}</span>
              <span>{formatPrice(total)}</span>
            </div>
            {walletApplied > 0 && (
              <div className="flex justify-between text-success">
                <span>Paid from wallet</span>
                <span>-{formatPrice(walletApplied)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold text-secondary pt-2 border-t border-gray-200">
              <span>{fullyPaidByWallet ? 'Total' : 'Amount due'}</span>
              <span>{formatPrice(fullyPaidByWallet ? total : amountDue)}</span>
            </div>
          </div>

          <p className="flex items-start gap-2 text-xs font-body text-muted-foreground px-1">
            <Zap className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary" aria-hidden="true" />
            Delivered instantly once payment clears — there's nothing to ship, so cash on
            delivery isn't available for gift cards.
          </p>

          <Button
            onClick={handleConfirm}
            disabled={processing}
            className="w-full rounded-full py-6 text-base"
            data-testid="giftcard-payment-confirm"
          >
            {processing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</>
            ) : fullyPaidByWallet
              ? `Pay ${formatPrice(walletApplied)} from Wallet`
              : `Pay ${formatPrice(amountDue)}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
